import Box from '@mui/material/Box';
import Drawer, { DrawerProps } from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemBase from '@mui/material/ListItem';
import ListItemTextBase from '@mui/material/ListItemText';
import ListItemButtonBase from '@mui/material/ListItemButton';
import { styled } from '@mui/material/styles';

export const DEFAULT_SIDE_NAV_WIDTH = '64px';

export interface SideNavDrawerProps extends DrawerProps {
  sideNavWidth?: string | number;
}

export const SideNavDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'sideNavWidth',
})<SideNavDrawerProps>(({ theme, sideNavWidth = DEFAULT_SIDE_NAV_WIDTH }) => ({
  width: sideNavWidth,
  zIndex: 100,
  height: '100vh',
  position: 'fixed',
  '& .MuiDrawer-paper': {
    width: sideNavWidth,
    backgroundColor: theme.palette.primary.main,
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      width: '280px',
    },
  },
  [theme.breakpoints.up('md')]: {
    '& .MuiDrawer-paper': { position: 'relative' },
  },
}));

export const Header = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  backgroundColor: theme.palette.primary.main,
}));

export const NavigationContent = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  top: theme.spacing(13.25),
  padding: theme.spacing(1, 0),
}));

export const LinkList = styled(List)(({ theme }) => ({
  flex: '1 1 0',
  margin: theme.spacing(1, 0),
  alignItems: 'center',
  justifyContent: 'center',
  overflowY: 'auto',
  overflowX: 'hidden',
  minHeight: 0,
  padding: 0,
  '& .MuiBadge-badge': {
    minWidth: theme.spacing(1.625),
    maxWidth: theme.spacing(1.625),
    maxHeight: theme.spacing(1.625),
    left: theme.spacing(0.625),
    top: theme.spacing(0.75),
    fontSize: theme.spacing(1),
  },
  '&::after': {
    content: '""',
    display: 'block',
    position: 'sticky',
    bottom: 0,
    left: 0,
    right: 0,
    height: theme.spacing(8),
    marginTop: theme.spacing(-8),
    background: `linear-gradient(to bottom, transparent, ${theme.palette.primary.main})`,
    pointerEvents: 'none',
    opacity: 0,
    transition: 'opacity 0.1s ease',
    animationName: 'scroll-fade',
    // Scroll-driven animation - requires modern browser support
    animationTimeline: 'scroll(nearest)',
    animationRange: '0% 100%',
    animationFillMode: 'forwards',
  },
  '@supports not (animation-timeline: auto)': {
    [`@media (max-height: ${theme.spacing(109)})`]: {
      '&::after': { opacity: 1, animation: 'none' },
    },
  },
  '@keyframes scroll-fade': {
    '0%': { opacity: 1 },
    '80%': { opacity: 1 },
  },
}));

export const BottomLinkList = styled(List)(() => ({
  flexShrink: 0,
  padding: 0,
}));

export const ListItem = styled(ListItemBase)(({ theme }) => ({
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    justifyContent: 'flex-start',
    width: '100%',
  },
}));

export const ListItemText = styled(ListItemTextBase, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  margin: theme.spacing(0),
  '& .MuiListItemText-primary': { width: theme.spacing(3) },
  '& .MuiListItemText-secondary, & .MuiListItemText-primary': {
    color: active ? theme.palette.primary.contrastText : 'rgba(255, 255, 255, 0.6)',
    fontSize: theme.spacing(1.25),
    fontWeight: 600,
    whiteSpace: 'normal',
    textAlign: 'center',
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    '& .MuiListItemText-primary': {
      marginRight: theme.spacing(2),
      width: 'auto',
    },
    '& .MuiListItemText-secondary': {
      fontSize: theme.spacing(1.75),
      textAlign: 'left',
      whiteSpace: 'nowrap',
    },
  },
}));

/** Polymorphic nav link — pass the `as` prop to swap the rendered element (e.g. as={RouterLink}). */
export const NavLink = styled('a')(({ theme }) => ({
  display: 'block',
  color: 'inherit',
  textDecoration: 'none',
  padding: `${theme.spacing(1)} ${theme.spacing(2.5)}`,
  width: '100%',
  transition: 'background-color 0.2s ease-out',
  ':hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    transition: 'background-color 0s',
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
    padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
  },
}));

export const ListItemButton = styled(ListItemButtonBase)(({ theme }) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(2.5)}`,
  width: '100%',
  transition: 'background-color 0.2s ease-out',
  ':hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    transition: 'background-color 0s',
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
    padding: `${theme.spacing(2)} ${theme.spacing(2)}`,
  },
}));

export const MobileMenuButton = styled(Box, {
  shouldForwardProp: (prop: string) => prop !== 'isOpen',
})<{ isOpen?: boolean }>(({ theme, isOpen }) => ({
  position: 'fixed',
  top: `calc(${theme.spacing(2)} - 8px)`,
  left: theme.spacing(2),
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: 'transparent',
  border: 'none',
  boxShadow: 'none',
  [theme.breakpoints.up('md')]: { display: 'none' },
  '& .MuiIconButton-root': {
    color: isOpen ? theme.palette.primary.contrastText : theme.palette.text.primary,
    padding: theme.spacing(1),
    border: 'none',
    boxShadow: 'none',
    backgroundColor: 'transparent !important',
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
      backgroundColor: 'transparent !important',
    },
    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
    '&:active': { backgroundColor: 'transparent !important' },
  },
}));
